import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Autocomplete,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Chip,
  CircularProgress,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import { Edit, Delete, Warehouse, LocalHospital } from "@mui/icons-material";
import ApiServices from "../../../Utils/ApiServices/ApiServices";
import { getDecryptedAccessToken } from "../../../Utils/LoginUserDetails.js/LoginUserSavedDetails";

const AllWarehouses = ({onWarehouseClick}) => {
  const accessToken = getDecryptedAccessToken();
  const [warehouses, setWarehouses] = useState([]);
  const [filteredWarehouses, setFilteredWarehouses] = useState([]);
  const [searchTitle, setSearchTitle] = useState("");
  const [selectedFacilities, setSelectedFacilities] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Fetch Warehouses
  const fetchWarehouses = async () => {
    setLoading(true);
    try {
      const response = await ApiServices.get("/apis/default/api/list/warehouse", {
        Authorization: `Bearer ${accessToken}`,
      });
      setWarehouses(response.data);
      setFilteredWarehouses(response.data);
    } catch (error) {
      showSnackbar("Error fetching warehouses", "error");
      setWarehouses([])
      setFilteredWarehouses([])
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchWarehouses();
  }, []);

  // Search and Filter Logic
  useEffect(() => {
    let filtered = warehouses.filter((w) =>
      w.title.toLowerCase().includes(searchTitle.toLowerCase())
    );

    if (selectedFacilities.length > 0) {
      filtered = filtered.filter((w) => selectedFacilities.includes(w.facility_name));
    }

    setFilteredWarehouses(filtered);
  }, [searchTitle, selectedFacilities, warehouses]);

  // Show Snackbar
  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  // Open Edit Dialog
  const handleEditClick = (warehouse) => {
    setEditData(warehouse);
    setEditDialogOpen(true);
  };

  // Close Edit Dialog
  const handleCloseEdit = () => {
    setEditDialogOpen(false);
    setEditData(null);
  };

  // Handle Edit API Call
  const handleEditSave = async () => {
    setProcessing(true);
    try {
      await ApiServices.put(
        `/apis/default/api/warehouse/${editData.option_id}/facility/${editData.option_value}`,
        {
          title: editData.title,
          notes: editData.notes,
          seq: editData.seq,
        },
        { Authorization: `Bearer ${accessToken}` }
      );
      showSnackbar("Warehouse updated successfully!", "success");
      fetchWarehouses();
      handleCloseEdit();
    } catch (error) {
      showSnackbar("Failed to update warehouse", "error");
    }
    setProcessing(false);
  };

  // Handle Delete
  const handleDelete = async (warehouse) => {
    const confirm = window.confirm(`Are you sure you want to delete warehouse: ${warehouse.title}?`);
    if (!confirm) return;

    setProcessing(true);
    try {
      await ApiServices.delete(
        `/apis/default/api/warehouse/${warehouse.option_id}/facility/${warehouse.option_value}`,
        { Authorization: `Bearer ${accessToken}` }
      );
      showSnackbar("Warehouse deleted successfully!", "success");
      fetchWarehouses();
    } catch (error) {
      showSnackbar("Failed to delete warehouse", "error");
    }
    setProcessing(false);
  };

  if(warehouses.length === 0){
    return(
        <Typography> No Data Available</Typography>
    )
  }

  return (
    <div>
      {/* Filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 15 }}>
        <TextField
          label="Search by Title"
          variant="outlined"
          size="small"
          fullWidth
          value={searchTitle}
          onChange={(e) => setSearchTitle(e.target.value)}
        />

        <Autocomplete
          multiple
          options={[...new Set(warehouses?.map((w) => w.facility_name))]}
          value={selectedFacilities}
          onChange={(e, newValue) => setSelectedFacilities(newValue)}
          renderInput={(params) => <TextField {...params} label="Filter by Facility" variant="outlined" size="small" />}
          fullWidth
        />
      </div>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Option ID</TableCell>
              <TableCell>Seq</TableCell>
              <TableCell>Facility Type</TableCell>
              <TableCell>Facility Name</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredWarehouses?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No Data Available
                </TableCell>
              </TableRow>
            ) : (
              filteredWarehouses?.map((warehouse) => (
                <TableRow key={warehouse.option_id}>
                  <TableCell onClick={() => onWarehouseClick(warehouse)}>{warehouse.title}</TableCell>
                  <TableCell>{warehouse.option_id}</TableCell>
                  <TableCell>{warehouse.seq}</TableCell>
                  <TableCell>
                    {warehouse.facility_type === "medspa" ? <LocalHospital color="primary" /> : <Warehouse color="secondary" />}{" "}
                    {warehouse.facility_type}
                  </TableCell>
                  <TableCell>{warehouse.facility_name}</TableCell>
                  <TableCell>{warehouse.notes || "N/A"}</TableCell>
                  <TableCell>
                    <Chip
                      label="Edit"
                      color="primary"
                      icon={<Edit />}
                      clickable
                      onClick={() => handleEditClick(warehouse)}
                    />
                    &nbsp;
                    <Chip
                      label="Delete"
                      color="error"
                      clickable
                      disabled={processing}
                      onClick={() => handleDelete(warehouse)}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => {}} disableEscapeKeyDown>
        <DialogTitle>Edit Warehouse</DialogTitle>
        <DialogContent>
          <TextField
            label="Title"
            fullWidth
            margin="dense"
            value={editData?.title || ""}
            onChange={(e) => setEditData({ ...editData, title: e.target.value })}
          />
          <TextField
            label="Notes"
            fullWidth
            margin="dense"
            value={editData?.notes || ""}
            onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
          />
          <TextField
            label="Sequence"
            fullWidth
            margin="dense"
            value={editData?.seq || ""}
            onChange={(e) => setEditData({ ...editData, seq: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit} color="secondary">
            Close
          </Button>
          <Button onClick={handleEditSave} color="primary" disabled={processing}>
            {processing ? <CircularProgress size={24} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notifications */}
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </div>
  );
};

export default AllWarehouses;
